import React from "react";
import { createRoot } from "react-dom/client";

import { JSONEditor } from "@json-editor/json-editor/dist/jsoneditor.js";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { CustomAlert } from "@shared/CustomAlert/CustomAlert";

const theme = createTheme();

type WarningWrapper = HTMLElement & { _reactRoot?: ReturnType<typeof createRoot> };

const appendWarningAlert = (container: HTMLElement, insertAfter: HTMLElement | null, text: string) => {
    const wrapper = document.createElement("div") as WarningWrapper;
    wrapper.setAttribute("data-json-editor-warning", "true");
    wrapper.style.marginTop = "12px";
    wrapper.style.marginBottom = "16px";

    if (insertAfter?.nextSibling) {
        container.insertBefore(wrapper, insertAfter.nextSibling);
    } else {
        container.appendChild(wrapper);
    }

    const root = createRoot(wrapper);
    wrapper._reactRoot = root;
    root.render(
        <ThemeProvider theme={theme}>
            <CustomAlert severity="warning">{text}</CustomAlert>
        </ThemeProvider>,
    );
};

const unmountWarningAlerts = (container: HTMLElement) => {
    const wrappers = container.querySelectorAll<WarningWrapper>("[data-json-editor-warning]");
    wrappers.forEach((wrapper) => {
        wrapper._reactRoot?.unmount();
    });
};

type ObjectEditorInstance = {
    options: { warning?: string };
    container: HTMLElement;
    description?: HTMLElement;
    translateProperty?: (key: string) => string;
};

const patchObjectEditorWarning = () => {
    const ObjectEditor = (
        JSONEditor as unknown as {
            defaults: {
                editors: {
                    object?: {
                        prototype: {
                            build?: () => void;
                            destroy?: () => void;
                            _warningPatched?: boolean;
                        };
                    };
                };
            };
        }
    ).defaults?.editors?.object;
    if (!ObjectEditor?.prototype?.build) {
        return;
    }
    if ((ObjectEditor.prototype as { _warningPatched?: boolean })._warningPatched) {
        return;
    }

    const originalBuild = ObjectEditor.prototype.build;
    ObjectEditor.prototype.build = function (this: ObjectEditorInstance) {
        originalBuild.call(this);
        const warning = this.options?.warning;
        if (!warning || !this.container) {
            return;
        }
        const text = typeof this.translateProperty === "function" ? this.translateProperty(warning) : warning;
        const insertAfter = this.description ?? this.container.querySelector("p");
        appendWarningAlert(this.container, insertAfter, text);
    };

    const originalDestroy = ObjectEditor.prototype.destroy;
    if (originalDestroy) {
        ObjectEditor.prototype.destroy = function (this: { container?: HTMLElement }) {
            if (this.container) {
                unmountWarningAlerts(this.container);
            }
            originalDestroy.call(this);
        };
    }

    (ObjectEditor.prototype as { _warningPatched?: boolean })._warningPatched = true;
};

export const initJsonEditorInfoTextSupport = () => {
    patchObjectEditorWarning();
};
